import { useEffect, useState } from "react";
import { Division, Select, Form, Flex, Span, Icon, Icons, Tooltip, Button } from "@ir/ui";
import "./style/addChargebreakdown.scss";
import useAddChargebreakdown from "./hooks/useAddChargebreakdown";
import { AddChargebreakdownItem } from "./class/AddChargebreakdownItem";
import { useWorkspace } from "@/hooks";

const MAX_MENU_HEIGHT = 150;

interface FormField {
  name: string;
  label: string;
  required?: boolean;
}

const FORM_FIELDS: FormField[] = [
  { name: "upcItemNumber", label: "UPC" },
  { name: "itemNumber", label: "Item Number" },
  { name: "totalAmount", label: "Total Amount", required: true },
  { name: "itemDescription", label: "Item Description" },
  { name: "distributionCenter", label: "DC" },
  { name: "orderQuantity", label: "Order Quantity" },
  { name: "quantity", label: "Quantity" },
  { name: "quantityDifference", label: "Quantity Difference" },
  { name: "packSize", label: "Pack Size" },
  { name: "unitCost", label: "Unit Cost" },
  { name: "receivedUnitCost", label: "Received Unit Cost" },
  { name: "costDifference", label: "Cost Difference" },
  { name: "allowance", label: "Allowance" },
  { name: "receivedDate", label: "Received Date" },
  { name: "expiryDate", label: "Expiry Date" },
  { name: "shipDate", label: "Ship Date" },
  { name: "poNumber", label: "PO Number" },
  { name: "poDate", label: "PO Date" },
  { name: "retailerName", label: "Retailer Name" },
  { name: "retailerNumber", label: "Retailer Number" },
  { name: "refInvoice", label: "Ref. Invoice" },
  { name: "invoiceAmount", label: "Invoice Amount" },
  { name: "invoiceDate", label: "Invoice Date" },
  { name: "discount", label: "Discount" },
  { name: "divisionName", label: "Division Name" },
  { name: "divisionNumber", label: "Division Number" },
];

const AddChargebreakdownFieldMapping = () => {
  const { finalChargebreakdownData, fieldMappingForm, setSelectedSteps, setCurrentStep, importedFileData, appHeadersMaster, setFinalChargebreakdownData } =
    useAddChargebreakdown();

  const { settings: workspaceSettings } = useWorkspace();

  useEffect(() => {
    if (importedFileData) {
      const getValues = fieldMappingForm.getFieldsValue(FORM_FIELDS.map(each => each.name));
      const getAllFieldValues = Object.values(getValues);

      if (getAllFieldValues.every(each => !each)) {
        const initialValues = FORM_FIELDS.reduce(
          (acc, field) => {
            acc[field.name] = getMappedHeader(field.name) ?? null;
            return acc;
          },
          {} as Record<string, any>
        );

        fieldMappingForm.setFieldsValue(initialValues);
      }

      checkMappedFields();
    } else {
      fieldMappingForm.resetFields();
    }
  }, [appHeadersMaster, importedFileData]);

  const getMappedHeader = (header: string) => {
    const fileHeaderOptions = getFileHeaderOptions();
    const findAppHeader = appHeadersMaster.find(appHeader => appHeader.key.toLowerCase() === header.toLowerCase());

    if (findAppHeader) {
      return fileHeaderOptions.find(eachFileHeader => getFindDictionaryLower(findAppHeader.findDictionary).includes(eachFileHeader.label.toLowerCase()));
    }

    return null;
  };

  const getFindDictionaryLower = (findDictionary: string[]) => {
    return findDictionary.map(eachFindDictionary => eachFindDictionary.toLowerCase());
  };

  const getFileHeaderOptions = () => {
    return importedFileData?.headers.filter(header => header !== "").map(header => ({ label: header, value: header })) || [];
  };

  const onFinish = () => {
    // Get the mapping from internal key to selected CSV header
    const fieldValues = fieldMappingForm.getFieldsValue(FORM_FIELDS.map(f => f.name));
    // Example: { upcItemNumber: { label: "UPC Item Number", value: "UPC Item Number" }, ... }

    // Build a mapping from internal key to CSV header
    const mapping: Record<string, string> = {};
    for (const key in fieldValues) {
      const selected = fieldValues[key];
      if (selected?.value) {
        mapping[key] = selected.value;
      }
    }

    // Normalize the data
    const normalizedData = importedFileData?.data.map(row => {
      const normalizedRow: any = {};
      for (const internalKey in mapping) {
        normalizedRow[internalKey] = row[mapping[internalKey]];
      }
      return normalizedRow;
    });

    const finalChargebreakdownDataBuild = normalizedData?.map(
      (item: any, idx: number) =>
        new AddChargebreakdownItem({
          chargebreakdownData: item,
          chargebreakdownIndex: idx,
          formatOptions: {
            dateFormat: workspaceSettings?.displayDateFormat,
            numberFormat: workspaceSettings?.displayNumberFormat,
          },
        })
    );

    // Set the normalized data for verification
    if (!finalChargebreakdownData) {
      setFinalChargebreakdownData(finalChargebreakdownDataBuild ?? null);
    }

    setSelectedSteps(["upload-chargebreakdown", "map-chargebreakdown", "verify-chargebreakdown"]);
    setCurrentStep("verify-chargebreakdown");
  };

  const [filedMappedCheck, setFiledMappedCheck] = useState(FORM_FIELDS.map(field => ({ fieldName: field.name, mapped: false })));

  const checkMappedFields = () => {
    const fieldValues = fieldMappingForm.getFieldsValue(FORM_FIELDS.map(field => field.name));
    setFiledMappedCheck(prev => {
      return prev.map(eachField => {
        if (fieldValues[eachField.fieldName]) {
          return { ...eachField, mapped: true };
        } else {
          return { ...eachField, mapped: false };
        }
      });
    });
  };

  const handleFormValuesChange = () => {
    setFinalChargebreakdownData(null);
  };

  if (!importedFileData) {
    return null;
  }

  return (
    <Division className="add-chargebreakdown-field-mapping">
      <Flex className="w-full header-row-wrap">
        <Flex className="header-row w-50" align="center">
          <Division className="cell" style={{ minWidth: "200px", textAlign: "left" }}>
            Import As
          </Division>
          <Division className="cell" style={{ minWidth: "390px", textAlign: "left" }}>
            File Column Header
          </Division>
          <Division className="cell" style={{ textAlign: "left" }}>
            Data Preview
          </Division>
        </Flex>
        <Flex className="header-row twoNd w-50">
          <Division className="cell" style={{ minWidth: "200px", textAlign: "left" }}>
            Import As
          </Division>
          <Division className="cell" style={{ minWidth: "390px", textAlign: "left" }}>
            File Column Header
          </Division>
          <Division className="cell" style={{ textAlign: "left" }}>
            Data Preview
          </Division>
        </Flex>
      </Flex>
      <Form form={fieldMappingForm} onValuesChange={handleFormValuesChange} onFieldsChange={checkMappedFields} onFinish={onFinish}>
        <Flex>
          {FORM_FIELDS.map((field, index) => (
            <Flex
              key={field.name}
              className={`body-row ${FORM_FIELDS.length % 2 !== 0 && index === FORM_FIELDS.length - 1 ? "w-100" : "w-50"}`}
              align="center"
              gap={1}
            >
              <Division className="cell" style={{ width: 170 }}>
                {field.label} {field.required ? <Span>*</Span> : ""}
              </Division>
              <Division className="cell">
                <Flex direction="row" gap={1} align="center">
                  {filedMappedCheck.find(eachField => eachField.fieldName === field.name)?.mapped ? (
                    <Tooltip content="Mapping found" triggerElement="span">
                      <Button noStyle style={{ cursor: "default" }}>
                        <Icon name={Icons.CheckCircleWithBg} />
                      </Button>
                    </Tooltip>
                  ) : (
                    <Tooltip content="No mapping found" triggerElement="span">
                      <Button noStyle style={{ cursor: "default" }}>
                        <Icon name={Icons.AlertCircleWithBg} />
                      </Button>
                    </Tooltip>
                  )}
                  <Form.Item name={field.name} rules={field.required ? [{ required: true, message: `${field.label} is required.` }] : []}>
                    <Select
                      className="select-field"
                      menuPlacement={index > 4 ? "top" : "bottom"}
                      options={getFileHeaderOptions()}
                      maxMenuHeight={MAX_MENU_HEIGHT}
                      isClearable={!field.required}
                    />
                  </Form.Item>
                </Flex>
              </Division>
              <Division className="cell">
                {(() => {
                  const mappedField = fieldMappingForm.getFieldValue(field.name);
                  const isMapped = filedMappedCheck.find(eachField => eachField.fieldName === field.name)?.mapped;
                  const fieldKey = mappedField?.value ?? mappedField;
                  const maxCharacters = 30;
                  const getDisplayValue = (value: any) => {
                    if (!isMapped || !fieldKey || (!value && value)) return "";
                    const currentValue = value?.toString() ?? "";
                    if (currentValue.length > maxCharacters) {
                      return (
                        <Tooltip content={currentValue} placement="top">
                          <Button className="map-data-preview" noStyle>
                            {currentValue.substring(0, maxCharacters)}...
                          </Button>
                        </Tooltip>
                      );
                    }
                    return (
                      <Button className="map-data-preview" noStyle style={{ cursor: "default" }}>
                        {currentValue}
                      </Button>
                    );
                  };
                  return importedFileData.data.slice(0, 2).map((item, index) => (
                    <Flex className="flex-1" align="center" key={item[fieldKey]?.toString() + index}>
                      {getDisplayValue(item[fieldKey])}
                    </Flex>
                  ));
                })()}
              </Division>
            </Flex>
          ))}
        </Flex>
      </Form>
    </Division>
  );
};

export default AddChargebreakdownFieldMapping;
